import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Card, CardContent, Grid, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import CustomizedInputs from '~/components/Input';
import { regexCPF, regexPhone } from '~/components/Input/regex';
import { DocumentType } from '~/constants/documentType';
import api from '~/services/api';
import { Insured } from '../../interfaces/contractinfo';
import { changeAddressBtn, internalCard, purpleText } from '../../styles';
function formatWithMask(raw: string, maskArr: Array<string | RegExp>) {
  const digits = raw.replace(/\D/g, '').split('');
  let di = 0;
  return maskArr.reduce<string>((out, m) => {
    if (typeof m === 'string') return out + m;
    if (di < digits.length) out += digits[di++];
    return out;
  }, '');
}

const maskMap: Record<'cpf' | 'phone', Array<string | RegExp>> = {
  cpf: regexCPF,
  phone: regexPhone
};

type FormValues = {
  document: string;
  birthdate: string;
  mobile_phone: string;
};

const schema = yup
  .object({
    document: yup
      .string()
      .required('CPF é obrigatório')
      .matches(/\d{3}\.\d{3}\.\d{3}-\d{2}/, 'CPF inválido'),
    mobile_phone: yup
      .string()
      .required('Celular é obrigatório')
      .matches(/\(\d{2}\)\s?9\d{4}-\d{4}/, 'Celular inválido'),
    birthdate: yup.date().required('Data de aniversário é obrigatória').max(new Date(), 'Data deve ser no passado')
  })
  .required();

export function InsuredInfoCard({
  insuredInfo,
  quoteId,
  organizationId
}: {
  insuredInfo: Insured;
  quoteId: number | undefined;
  organizationId?: string;
}) {
  const [editMode, setEditMode] = useState(false);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      document: formatWithMask(insuredInfo.document, maskMap.cpf),
      mobile_phone: formatWithMask(insuredInfo.mobile_phone, maskMap.phone),
      birthdate: insuredInfo.birthdate ? insuredInfo.birthdate.slice(0, 10) : ''
    }
  });

  useEffect(() => {
    reset({
      document: formatWithMask(insuredInfo.document, maskMap.cpf),
      mobile_phone: formatWithMask(insuredInfo.mobile_phone, maskMap.phone),
      birthdate: insuredInfo.birthdate ? insuredInfo.birthdate.slice(0, 10) : ''
    });
  }, [insuredInfo, reset]);

  const onSubmit = async (data: FormValues) => {
    const payload: Record<string, unknown> = {
      name: typeof insuredInfo.name === 'string' ? insuredInfo.name.trim() : insuredInfo.name,
      email: typeof insuredInfo.email === 'string' ? insuredInfo.email.trim() : insuredInfo.email,
      mobile_phone: data.mobile_phone.replace(/\D/g, ''),
      document: data.document.replace(/\D/g, ''),
      document_type: DocumentType.NotaFiscaOuDeclaracaoDePropriedade,
      birthdate: new Date(data.birthdate).toISOString()
    };
    if (organizationId) payload.organization_id = organizationId;
    try {
      await api.v2!.put(`/Quotes/${quoteId}/customer`, payload);
    } catch {
    } finally {
      setEditMode(false);
    }
  };

  return (
    <Card sx={{ m: { xs: '2rem .5rem', sm: '2rem 1rem', md: '2rem 2rem' }, mt: 0 }}>
      <CardContent sx={internalCard}>
        <Typography variant="h6" align="left" sx={{ pb: 1 }}>
          2 - Dados do segurado
        </Typography>

        {editMode ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Nome completo:
                </Typography>
                <Typography variant="subtitle2">{insuredInfo.name}</Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  CPF:
                </Typography>
                <Controller
                  name="document"
                  control={control}
                  render={({ field, fieldState }) => (
                    <CustomizedInputs
                      {...field}
                      mask="cpf"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      isDirty={!!field.value}
                      label=""
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Data de aniversário:
                </Typography>
                <Controller
                  name="birthdate"
                  control={control}
                  render={({ field, fieldState }) => (
                    <CustomizedInputs
                      {...field}
                      type="date"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      isDirty={!!field.value}
                      label=""
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  Celular:
                </Typography>
                <Controller
                  name="mobile_phone"
                  control={control}
                  render={({ field, fieldState }) => (
                    <CustomizedInputs
                      {...field}
                      mask="phone"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      isDirty={!!field.value}
                      label=""
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1" sx={purpleText}>
                  E-mail:
                </Typography>
                <Typography variant="subtitle2">{insuredInfo.email}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Button type="submit" sx={changeAddressBtn}>
                  Confirmar
                </Button>
              </Grid>
            </Grid>
          </form>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle1" sx={purpleText}>
                Nome completo:
              </Typography>
              <Typography variant="subtitle2">{insuredInfo.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle1" sx={purpleText}>
                CPF:
              </Typography>
              <Typography variant="subtitle2">{formatWithMask(insuredInfo.document, maskMap.cpf)}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle1" sx={purpleText}>
                Data de aniversário:
              </Typography>
              {insuredInfo.birthdate && (
                <Typography variant="subtitle2">{format(parseISO(insuredInfo.birthdate), 'dd/MM/yyyy')}</Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle1" sx={purpleText}>
                Celular:
              </Typography>
              <Typography variant="subtitle2">{formatWithMask(insuredInfo.mobile_phone, maskMap.phone)}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle1" sx={purpleText}>
                E-mail:
              </Typography>
              <Typography variant="subtitle2">{insuredInfo.email}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Button onClick={() => setEditMode(true)} sx={changeAddressBtn}>
                Alterar
              </Button>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}
